import { State } from '@meraki-internal/state';
import { ISmartStorageProvider, LocalStorageProvider } from './LocalStorageProvider';

export interface IDevSettingsState {
    useStaging: boolean;
    simulateNative: boolean;
    enableHexagonTools: boolean;
    eligibleForTrial: boolean;
    enableWebBypassUpgrade: boolean;
    useRealPaymentsEvenIfBypassEnabled: boolean;
    showVeryShortExpiration: boolean;
    disableToastAutoDismiss: boolean;
    simulateTasksStatus: boolean;
    showApptRecordingNotificationBtn: boolean;
    recordingShortPauseAutoSave: boolean;
    disableRecordingContextStart: boolean;
    enableUploadRecording: boolean;
    simulateRecordingSuccess: boolean;
    ignoreShortRecordingMessages: boolean;
    disableRecordingProcessing: boolean;
    showTaskNotificationTestBtns: boolean;
    simulateIndexedDBFailure: boolean;
    simulateFilesystemFailure: boolean;
}

export class DevSettingsStorage {
    settings: ISmartStorageProvider<{[key: string]: boolean}>;
    static inject = () => [LocalStorageProvider];
    constructor(private storage: LocalStorageProvider) {
        this.settings = this.storage.getJSONProvider('dev-settings', { userNeutral: true, envNeutral: true });
    }
}

export class DevSettings extends State<IDevSettingsState> {
    static inject = () => [DevSettingsStorage];

    constructor(private storage: DevSettingsStorage) {
        const settings = storage.settings.get() || {};
        super({
            useStaging: settings.useStaging || false,
            simulateNative: settings.simulateNative || false,
            enableHexagonTools: settings.enableHexagonTools || false,
            eligibleForTrial: settings.eligibleForTrial || false,
            enableWebBypassUpgrade: settings.enableWebBypassUpgrade || false,
            useRealPaymentsEvenIfBypassEnabled: settings.useRealPaymentsEvenIfBypassEnabled || false,
            showVeryShortExpiration: settings.showVeryShortExpiration || false,
            disableToastAutoDismiss: settings.disableToastAutoDismiss || false,
            simulateTasksStatus: settings.simulateTasksStatus || false,
            showApptRecordingNotificationBtn: settings.showApptRecordingNotificationBtn || false,
            recordingShortPauseAutoSave: settings.recordingShortPauseAutoSave || false,
            disableRecordingContextStart: settings.disableRecordingContextStart || false,
            enableUploadRecording: settings.enableUploadRecording || false,
            simulateRecordingSuccess: settings.simulateRecordingSuccess || false,
            ignoreShortRecordingMessages: settings.ignoreShortRecordingMessages || false,
            disableRecordingProcessing: settings.disableRecordingProcessing || false,
            showTaskNotificationTestBtns: settings.showTaskNotificationTestBtns || false,
            simulateIndexedDBFailure: settings.simulateIndexedDBFailure || false,
            simulateFilesystemFailure: settings.simulateFilesystemFailure || false
        });
    }

    // Getter methods for backwards compatibility
    get useStaging() { return this.state.useStaging; }
    get simulateNative() { return this.state.simulateNative; }
    get enableHexagonTools() { return this.state.enableHexagonTools; }
    get eligibleForTrial() { return this.state.eligibleForTrial; }
    get enableWebBypassUpgrade() { return this.state.enableWebBypassUpgrade; }
    get useRealPaymentsEvenIfBypassEnabled() { return this.state.useRealPaymentsEvenIfBypassEnabled; }
    get showVeryShortExpiration() { return this.state.showVeryShortExpiration; }
    get disableToastAutoDismiss() { return this.state.disableToastAutoDismiss; }
    get simulateTasksStatus() { return this.state.simulateTasksStatus; }
    get showApptRecordingNotificationBtn() { return this.state.showApptRecordingNotificationBtn; }
    get recordingShortPauseAutoSave() { return this.state.recordingShortPauseAutoSave; }
    get disableRecordingContextStart() { return this.state.disableRecordingContextStart; }
    get enableUploadRecording() { return this.state.enableUploadRecording; }
    get simulateRecordingSuccess() { return this.state.simulateRecordingSuccess; }
    get ignoreShortRecordingMessages() { return this.state.ignoreShortRecordingMessages; }
    get disableRecordingProcessing() { return this.state.disableRecordingProcessing; }
    get showTaskNotificationTestBtns() { return this.state.showTaskNotificationTestBtns; }
    get simulateIndexedDBFailure() { return this.state.simulateIndexedDBFailure; }
    get simulateFilesystemFailure() { return this.state.simulateFilesystemFailure; }
}
export class DevSettingsWriter {
    static inject = () => [
        DevSettings,
        DevSettingsStorage
    ];
    constructor(
        private reader: DevSettings,
        private storage: DevSettingsStorage
    ){ };

    setSetting = async (key: string, value: any) => {
        this.reader.setState({ [key]: value } as Partial<IDevSettingsState>);
        this.persistLocalStorage();
    };

    private persistLocalStorage = () => {
        this.storage.settings.set({
            useStaging: this.reader.useStaging,
            simulateNative: this.reader.simulateNative,
            enableHexagonTools: this.reader.enableHexagonTools,
            eligibleForTrial: this.reader.eligibleForTrial,
            enableWebBypassUpgrade: this.reader.enableWebBypassUpgrade,
            useRealPaymentsEvenIfBypassEnabled: this.reader.useRealPaymentsEvenIfBypassEnabled,
            showVeryShortExpiration: this.reader.showVeryShortExpiration,
            simulateTasksStatus: this.reader.simulateTasksStatus,
            showApptRecordingNotificationBtn: this.reader.showApptRecordingNotificationBtn,
            recordingShortPauseAutoSave: this.reader.recordingShortPauseAutoSave,
            disableRecordingContextStart: this.reader.disableRecordingContextStart,
            enableUploadRecording: this.reader.enableUploadRecording,
            simulateRecordingSuccess: this.reader.simulateRecordingSuccess,
            ignoreShortRecordingMessages: this.reader.ignoreShortRecordingMessages,
            disableRecordingProcessing: this.reader.disableRecordingProcessing,
            showTaskNotificationTestBtns: this.reader.showTaskNotificationTestBtns,
            simulateIndexedDBFailure: this.reader.simulateIndexedDBFailure,
            simulateFilesystemFailure: this.reader.simulateFilesystemFailure
        });
    };
};
